import PaymentIcon from '@mui/icons-material/Payment'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { RetailerAccount } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { memo, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import campaignContentPaths from '@src/components/content/campaignContentPaths'
import LinkChild from '@src/lib/LinkChild'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import { formatWithoutCents } from '@src/lib/Utils'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'
import { availablePaidAdsByDateSelector } from '@src/stores/paidAds/selectors'

import DepositFundsButton from '../payment/DepositFundsButton'
import ContentWithTooltip from '../shared/ContentWithTooltip'

import { MAX_PER_SECTION, RandomAdFromRedux } from './constants'
import useRetailerAccountWallet from './useRetailerAccountWallet'
import WalletBrandFunds, { BrandLedgerGrouping } from './WalletBrandFunds'
import WalletPaidAds from './WalletPaidAds'

const WalletButton: React.FC<React.PropsWithChildren> = () => {
  const isStripeDepositsEnabled = useFeatureFlag('allow-stripe-deposits')
  const [showMore, setShowMore] = useState(false)
  const { findBrandLedgers, findSelfFundedLedgers } = useRetailerAccountWallet()
  const currentRetailerAccount: RetailerAccount = useSelector(
    currentRetailerAccountSelector,
  )

  const paidAds: RandomAdFromRedux[] = useSelector(
    availablePaidAdsByDateSelector,
  )
  const filteredPaidAds = showMore ? paidAds : paidAds.slice(0, MAX_PER_SECTION)
  const campaignFundsTotals = paidAds.reduce((prev, ad) => {
    return prev + ad.creditAmount
  }, 0)

  const { ledgersByBrand, brandFundsTotal } = useMemo(() => {
    const brandFunds = findBrandLedgers({
      excludeExpired: true,
      excludeEmpty: true,
    })
    const brandFundsTotal = brandFunds.reduce((prev, ledger) => {
      return prev + Number(ledger.balance)
    }, 0)

    // The order is somewhat important here, so use a map instead of an object.
    const map = new Map<string, BrandLedgerGrouping>()

    for (const ledger of brandFunds) {
      const brandUuid = ledger.restrictions[0]?.filter_value || ''
      let fromMap = map.get(brandUuid)

      if (!fromMap) {
        fromMap = {
          brandUuid,
          ledgers: [],
        }

        map.set(brandUuid, fromMap)
      }

      fromMap.ledgers.push(ledger)
    }

    return { ledgersByBrand: Array.from(map.values()), brandFundsTotal }
  }, [findBrandLedgers])
  const filteredLedgersByBrand = showMore
    ? ledgersByBrand
    : ledgersByBrand.slice(0, MAX_PER_SECTION)

  const selfFundedLedgers = findSelfFundedLedgers({
    excludeExpired: true,
    excludeEmpty: true,
  })
  const selfFundedTotal = selfFundedLedgers.reduce((memo, x) => {
    return memo + Number(x.balance)
  }, 0)

  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const handleClick: React.MouseEventHandler = () => {
    setIsOpen((prev) => !prev)
  }
  const showMoreButton = (
    <Button fullWidth size="small" onClick={() => setShowMore((prev) => !prev)}>
      {showMore ? I18n.t('shared.showLess') : I18n.t('shared.showMore')}
    </Button>
  )

  const theme = useTheme()
  const isConstrained = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <>
      <Typography
        variant="body2"
        sx={{ display: { xs: 'none', md: 'initial' }, cursor: 'pointer' }}
        onClick={handleClick}
      >
        {formatWithoutCents.format(
          (campaignFundsTotals + brandFundsTotal + selfFundedTotal) / 100,
        )}
      </Typography>

      <IconButton
        color="inherit"
        size="large"
        ref={anchorRef}
        onClick={handleClick}
      >
        <PaymentIcon />
      </IconButton>

      <Popover
        anchorEl={anchorRef.current}
        // data-testid={`${name}DropdownContainer`}
        // id={id}
        onClose={() => {
          setIsOpen(false)
        }}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            // padding: 2,
            width: {
              xs: 300,
              md: 900,
            },
            overflowY: 'auto',
            maxHeight: '80vh',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          marginBottom={4}
          padding={2}
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Typography variant="h6">{I18n.t('wallet.title')}</Typography>

          <div>
            <LinkChild
              to={campaignContentPaths.index.build({
                retailerAccountSlug: currentRetailerAccount?.slug || '',
                activeTab: 'paid',
              })}
            >
              <Button color="premiumAds" variant="contained">
                {I18n.t('wallet.goToPaid')}
              </Button>
            </LinkChild>
          </div>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          padding={2}
          divider={
            isConstrained ? (
              <Divider />
            ) : (
              <Divider orientation="vertical" flexItem />
            )
          }
          spacing={4}
        >
          <div style={{ flex: '1 1 auto' }}>
            <ContentWithTooltip title={I18n.t('wallet.campaignFunds.tooltip')}>
              <strong>
                {I18n.t('wallet.campaignFunds.title')}:{' '}
                <Box component="span" color="success.main">
                  {formatWithoutCents.format(campaignFundsTotals / 100)}
                </Box>
              </strong>
            </ContentWithTooltip>

            <WalletPaidAds paidAds={filteredPaidAds} />

            {paidAds.length > MAX_PER_SECTION ? showMoreButton : undefined}
          </div>

          <div style={{ flex: '1 1 auto' }}>
            <ContentWithTooltip title={I18n.t('wallet.brandFunds.tooltip')}>
              <strong>
                {I18n.t('wallet.brandFunds.title')}:{' '}
                <Box component="span" color="success.main">
                  {formatWithoutCents.format(brandFundsTotal / 100)}
                </Box>
              </strong>
            </ContentWithTooltip>

            <WalletBrandFunds brandFunds={filteredLedgersByBrand} />

            {ledgersByBrand.length > MAX_PER_SECTION
              ? showMoreButton
              : undefined}
          </div>

          {isStripeDepositsEnabled ? (
            <div
              style={{
                flex: '.5 .5 auto',
              }}
            >
              <Stack direction="column" sx={{ height: '100%' }}>
                <ContentWithTooltip
                  title={I18n.t('wallet.retailerFunds.tooltip')}
                >
                  <strong>
                    {I18n.t('wallet.retailerFunds.title')}:{' '}
                    <Box component="span" color="success.main">
                      {formatWithoutCents.format(selfFundedTotal / 100)}
                    </Box>
                  </strong>
                </ContentWithTooltip>

                <div
                  style={{
                    display: 'flex',
                    flex: '1 0 auto',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DepositFundsButton />
                </div>
              </Stack>
            </div>
          ) : undefined}
        </Stack>
      </Popover>
    </>
  )
}

export default memo(WalletButton)
